import React, { useState, useEffect } from 'react';
import { Modal } from '../Common/Modal';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { FiX, FiSave, FiCheckCircle } from 'react-icons/fi';
import dcfService from '../../services/dcfService';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { dcf as dcfMessages, common as commonMessages } from '../../utils/validationMessages';
import { formatDateTimeDDMMMYYYY } from '../../utils/dateFormat';
import './DCFModal.css';

const DCFModal = ({ isOpen, onClose, reportId, sectionName, questionNumber, questionText, existingDCFs = [] }) => {
  const [dcfs, setDcfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [category, setCategory] = useState('');
  const [confirmCloseDCF, setConfirmCloseDCF] = useState({ open: false, dcfId: null });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && reportId) {
      fetchDCFs();
      setRemarks('');
      setRemarksError('');
      setPriority('Medium');
      setCategory('');
      setError(null);
    }
  }, [isOpen, reportId]);

  const fetchDCFs = async () => {
    try {
      setLoading(true);
      const data = await dcfService.getByReport(reportId);
      // Filter DCFs for this specific section/question if provided
      let filteredDCFs = data || [];
      if (sectionName) {
        filteredDCFs = filteredDCFs.filter(
          dcf => dcf.sectionName === sectionName && 
          (!questionNumber || dcf.questionNumber === questionNumber)
        );
      }
      setDcfs(filteredDCFs);
    } catch (err) {
      console.error('Error fetching DCFs:', err);
      setError(err.response?.data?.error || "Failed to load Reviewer's Comments");
    } finally {
      setLoading(false);
    }
  };

  const hasOpenDCF = (dcfs || []).some(d => (d.status || '').toLowerCase() !== 'closed');

  const handleRaiseDCF = async (e) => {
    e.preventDefault();

    if (hasOpenDCF) {
      setError(dcfMessages.alreadyRaised);
      return;
    }
    if (!remarks.trim()) {
      setRemarksError('Please Enter Remarks.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setRemarksError('');

      await dcfService.create({
        reportID: reportId,
        sectionName: sectionName || 'General',
        questionNumber: questionNumber || null,
        questionText: questionText || null,
        remarks: remarks.trim(),
        priority: priority,
        category: category || null,
      });

      setRemarks('');
      setPriority('Medium');
      setCategory('');
      setToast({ type: 'success', message: commonMessages.authSuccess });
      await fetchDCFs();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to raise Reviewer's Comment");
      console.error('Error raising DCF:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseDCFClick = (dcfId) => {
    setConfirmCloseDCF({
      open: true,
      dcfId,
      onConfirm: () => {
        const id = dcfId;
        setConfirmCloseDCF({ open: false, dcfId: null });
        if (id != null) handleCloseDCF(id);
      },
    });
  };

  const handleCloseDCF = async (dcfId) => {
    if (dcfId == null) return;
    try {
      setSubmitting(true);
      setError(null);
      await dcfService.close(dcfId);
      setToast({ type: 'success', message: commonMessages.authSuccess });
      await fetchDCFs();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to close Reviewer's Comment");
      console.error('Error closing DCF:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return formatDateTimeDDMMMYYYY(dateString);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'badge-warning';
      case 'responded':
        return 'badge-info';
      case 'closed':
        return 'badge-success';
      default:
        return 'badge-secondary';
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={submitting}
      >
        <FiX size={20} />
        Close
      </button>
    </>
  );

  return (
    <>
    {toast && (
      <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reviewer's Comments Detail"
      size="large"
      footer={footer}
      className="dcf-modal"
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {error && <Alert type="error" message={error} />}

          {/* DCF History Table */}
          <div className="dcf-table-container">
            <table className="dcf-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Remarks</th>
                  <th>Performed By</th>
                  <th>Performed On UTC</th>
                  <th>Performed At Offset</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dcfs.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                      No Reviewer's Comments found
                    </td>
                  </tr>
                ) : (
                  dcfs.map((dcf) => (
                    <tr key={dcf.dcfID}>
                      <td>
                        {dcf.dcfStatus === 'Open' ? "Reviewer's Comment Raised" : 
                         dcf.dcfStatus === 'Responded' ? "Reviewer's Comment Responded" : 
                         "Reviewer's Comment Closed"}
                      </td>
                      <td>
                        <div className="dcf-remarks">
                          <strong>Initial:</strong> {dcf.remarks || ''}
                          {dcf.response && (
                            <>
                              <br />
                              <strong>Response:</strong> {dcf.response}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {dcf.raisedByName || '—'}
                        {dcf.respondedByName && (
                          <>
                            <br />
                            <small>Response: {dcf.respondedByName}</small>
                          </>
                        )}
                      </td>
                      <td>{formatDate(dcf.raisedDate)}</td>
                      <td>+05:30</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(dcf.dcfStatus)}`}>
                          {dcf.dcfStatus}
                        </span>
                      </td>
                      <td>
                        {dcf.dcfStatus !== 'Closed' && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleCloseDCFClick(dcf.dcfID)}
                            disabled={submitting}
                            title="Close Reviewer's Comment"
                          >
                            <FiCheckCircle size={16} />
                            Close
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Raise New DCF Form */}
          <div className="dcf-form-section">
            <h4>Raise New Reviewer's Comment</h4>
            <form onSubmit={handleRaiseDCF}>
              <div className="form-group dcf-comment-box-wrap">
                <label className="form-label">
                  Remarks <span className="required-asterisk">*</span>
                </label>
                <textarea
                  className="form-control dcf-comment-box"
                  rows="4"
                  value={remarks}
                  onChange={(e) => { setRemarks(e.target.value); setRemarksError(''); }}
                  aria-label="Reviewer's Comment remarks or comment"
                />
                {remarksError && (
                  <div className="field-error text-danger mt-1" style={{ fontSize: '12px' }}>
                    {remarksError}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-control"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Data Quality, Protocol Deviation"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  <FiSave size={20} />
                  {submitting ? 'Raising...' : "Raise Reviewer's Comment"}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </Modal>
    {confirmCloseDCF.open && (
      <ConfirmDialog
        isOpen={true}
        onClose={() => setConfirmCloseDCF({ open: false, dcfId: null })}
        onConfirm={confirmCloseDCF.onConfirm}
        title="Close Reviewer's Comment"
        message="Are you sure you want to close this Reviewer's Comment?"
        confirmText="Yes"
        cancelText="No"
        variant="danger"
      />
    )}
  </>
  );
};

export default DCFModal;
